import {
    Box,
    Button,
    Checkbox,
    FormControl,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { observer } from 'mobx-react-lite';
import React, { useContext, useEffect } from 'react';
import { IDTransactionLog } from 'realhaus-sdk';
import StringUtils from '../../utils/string';
import { RootStoreContext } from '../../global/storeContext';
import { TransactionStatus, TrxLogType } from 'realhaus-sdk';
import { AdminDrawer, IAnchorType } from '../uiComponent/drawer';
import { TransactionDrawer } from './drawers/transactionDrawer';

const trxStatuses = Object.entries(TransactionStatus).map((entry) => {
    return { key: entry[0], text: entry[1] };
});
const trxTypes = Object.entries(TrxLogType).map((entry) => {
    return { key: entry[0], text: entry[1] };
});

export const Transactions: React.FC = observer(() => {
    const [trxLogs, setTrxLogs] = React.useState<IDTransactionLog[]>([]);
    const [cursor, setCursor] = React.useState<{ id: string; timestamp: number } | undefined>();
    const { dashboardStore, uiStore } = useContext(RootStoreContext);
    const [trxStatusFilter, setTrxStatusFilter] = React.useState<TransactionStatus[]>(
        trxStatuses.map((status) => status.text),
    );
    const [trxTypeFilter, setTrxTypeFilter] = React.useState<TrxLogType[]>(
        trxTypes.map((type) => type.text),
    );
    const [trxLogIdText, setTrxLogIdText] = React.useState<string>();
    const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
    const [selectedTrxLog, setSelectedTrxLog] = React.useState<IDTransactionLog>();

    const getTransactionLogs = (isFetchMore?: boolean) => {
        uiStore.showLoading();
        // we want to confirm a user wants to fetch more data based on the same query params
        const startAfter = !!isFetchMore ? cursor : undefined;

        dashboardStore
            .getTransactionLogs(startAfter, trxLogIdText, trxStatusFilter, trxTypeFilter)
            .then((response) => {
                if (response) {
                    if (isFetchMore) {
                        let transactionLogs = [...trxLogs];
                        response.trxLogs.forEach((trx) => {
                            if (transactionLogs.find((t) => t.id === trx.id)) return;
                            transactionLogs.push(trx);
                        });
                        setTrxLogs(transactionLogs);
                    } else {
                        setTrxLogs(response.trxLogs);
                    }
                    setCursor(response.cursor);
                    return;
                }
                uiStore.error('No transaction logs request');
            })
            .catch((err) => {
                uiStore.error('Could not fetch transaction log requests');
                console.log(err);
            })
            .finally(() => {
                uiStore.hideLoading();
            });
    };

    useEffect(() => {
        getTransactionLogs();
    }, [trxLogIdText, trxStatusFilter, trxTypeFilter]);

    const handleTrxStatusFilterChange = (event: SelectChangeEvent<string[]>) => {
        let filtTrxStatus = event.target.value as TransactionStatus[];
        filtTrxStatus = filtTrxStatus.length === 0 ? trxStatuses.map((t) => t.text) : filtTrxStatus;
        setTrxStatusFilter(filtTrxStatus);
    };

    const handleTrxTypeFilterChange = (event: SelectChangeEvent<string[]>) => {
        let filtTrxTypes = event.target.value as TrxLogType[];
        filtTrxTypes = filtTrxTypes.length === 0 ? trxTypes.map((t) => t.text) : filtTrxTypes;
        setTrxTypeFilter(filtTrxTypes);
    };

    const handleTransactionLogIdChange = (event: React.ChangeEvent<{ value: string }>) => {
        const val = event.target.value;
        setTrxLogIdText(val);
    };

    const handleDrawerOpen = (trxlog: IDTransactionLog) => {
        const isSelection = window.getSelection()?.toString();
        if (!!isSelection) {
            return;
        }
        setSelectedTrxLog(trxlog);
        setDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setSelectedTrxLog(undefined);
        setDrawerOpen(false);
    };

    return (
        <div>
            <Grid container spacing={5}>
                <Grid item xs={12}>
                    <Typography variant='h4'>Transactions</Typography>
                </Grid>
                <Grid container item xs={12} alignItems='center'>
                    <Grid item xs={4}>
                        <Box display='flex' alignItems='center' justifyItems='center'>
                            <Typography mr={3}>Find:</Typography>
                            <TextField
                                id='standard-basic'
                                label='Transaction Log Id'
                                variant='standard'
                                value={trxLogIdText}
                                onChange={handleTransactionLogIdChange}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={8}>
                        <Box display='flex' alignItems='center' justifyItems='center'>
                            <Typography mr={3}>Filter:</Typography>
                            <FormControl
                                variant='outlined'
                                size='small'
                                style={{
                                    minWidth: '250px',
                                    maxWidth: '300px',
                                    marginRight: '10px',
                                }}
                            >
                                <Select
                                    id='transaction-status-filter'
                                    variant='outlined'
                                    multiple
                                    value={trxStatusFilter}
                                    onChange={handleTrxStatusFilterChange}
                                    renderValue={(selected) => (selected as string[]).join(', ')}
                                >
                                    {trxStatuses.map((status) => (
                                        <MenuItem key={status.key} value={status.key}>
                                            <Checkbox
                                                checked={
                                                    !!trxStatusFilter.find((f) => f === status.text)
                                                }
                                            />
                                            <ListItemText primary={status.text} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl
                                variant='outlined'
                                size='small'
                                style={{ minWidth: '250px', maxWidth: '300px' }}
                            >
                                <Select
                                    id='transaction-type-filter'
                                    variant='outlined'
                                    multiple
                                    value={trxTypeFilter}
                                    onChange={handleTrxTypeFilterChange}
                                    renderValue={(selected) => (selected as string[]).join(', ')}
                                >
                                    {trxTypes.map((type) => (
                                        <MenuItem key={type.key} value={type.key}>
                                            <Checkbox
                                                checked={
                                                    !!trxTypeFilter.find((f) => f === type.text)
                                                }
                                            />
                                            <ListItemText primary={type.text} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                            <TableHead>
                                <TableRow>
                                    <TableCell align='center'>
                                        <b>Date</b>
                                    </TableCell>
                                    <TableCell align='center'>
                                        <b>Type & Id</b>
                                    </TableCell>
                                    <TableCell align='center'>
                                        <b>Vendor & ReferenceId</b>
                                    </TableCell>
                                    <TableCell align='center'>
                                        <b>Status</b>
                                    </TableCell>
                                    <TableCell align='center'>
                                        <b>Amount</b>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {trxLogs
                                    .sort((a, b) => b.timestamp - a.timestamp)
                                    .map((trxLog) => (
                                        <TableRow
                                            key={trxLog.id}
                                            sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                            }}
                                            onMouseUp={() => handleDrawerOpen(trxLog)}
                                        >
                                            <TableCell component='th' scope='row' align='center'>
                                                {StringUtils.getDateTimeFormat(trxLog.timestamp)}
                                            </TableCell>
                                            <TableCell align='center'>
                                                <p>{trxLog.type}</p>
                                                <p>{trxLog.id}</p>
                                            </TableCell>
                                            <TableCell align='center'>
                                                <p>{trxLog.source}</p>
                                                <p>{trxLog.referenceId}</p>
                                            </TableCell>
                                            <TableCell component='th' scope='row' align='center'>
                                                {trxLog.status}
                                            </TableCell>
                                            <TableCell align='center'>
                                                {StringUtils.asCurrency(trxLog.amount)}
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                <TableRow>
                                    <TableCell align='center' colSpan={4}>
                                        <Button
                                            disabled={!cursor}
                                            onClick={() => {
                                                getTransactionLogs(true);
                                            }}
                                        >
                                            ...Load more
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <AdminDrawer
                        type={IAnchorType.RIGHT}
                        open={drawerOpen}
                        onClose={handleDrawerClose}
                    >
                        <TransactionDrawer transactionLog={selectedTrxLog} />
                    </AdminDrawer>
                </Grid>
            </Grid>
        </div>
    );
});
